/**
 * Client für Mängelansprüche — `/:customer/claims…` und `/:customer/defects/:defect/limitation` auf
 * tri-server (docs/claims.md). Hinter dem Server-Schalter `CLAIMS_ENABLED`: aus heißt 404, dann
 * zeigt das Portal die Anspruchsteile gar nicht erst (`null` statt Fehler).
 *
 * Jede Verjährung ist eine fachliche Fristberechnung, **keine Rechtsberatung** — der Server liefert
 * den Hinweis (`noLegalAdvice`) mit, und das Portal zeigt ihn an jeder Stelle, an der ein Ende steht.
 */

import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

/** Kalendertag `YYYY-MM-DD`, wie ihn die Engine rechnet — nie eine Uhrzeit. */
export type LegalDate = string;

export type LimitationState = "notStarted" | "running" | "expired" | "unknown";

export interface LimitationStep {
  rule: string;
  before: LegalDate | null;
  after: LegalDate | null;
  note: string;
}

export interface LimitationNote {
  code: string;
  text: string;
  action?: string;
}

export interface Limitation {
  state: LimitationState;
  start: LegalDate | null;
  end: LegalDate | null;
  earliestEnd: LegalDate | null;
  remainingDays: number | null;
  baseMonths: number | null;
  ruleSetVersion: string;
  legallyApproved: boolean;
  asOf: LegalDate;
  steps: LimitationStep[];
  notes: LimitationNote[];
}

export interface DefectLimitation {
  defect: string;
  claimStatus: string | null;
  claimActive: boolean;
  endedBy: "discarded" | null;
  contract: string | null;
  acceptance?: string | null;
  limitation: Limitation | null;
  reason: "noContract" | "contractNotFound" | null;
  noLegalAdvice: string;
}

export interface ConstructionContractView {
  _id: string;
  name: string;
  lot?: string | null;
  legalType?: "bgbWork" | "vobB" | null;
  workKind?: string | null;
  agreedLimitationMonths?: number | null;
  acceptanceRecords?: Array<{ _id: string; date?: string; kind?: string; partial?: boolean }>;
}

export interface AtRiskClaim {
  defect: string;
  mark: string | null;
  title: string | null;
  asset: string | null;
  assetName: string | null;
  contract: string;
  contractName: string | null;
  limitationEnd: LegalDate;
  remainingDays: number;
  stage: number;
  uncertain: boolean;
  notes: string[];
}

export interface ClaimsAtRisk {
  asOf: LegalDate;
  days: number;
  claims: AtRiskClaim[];
  byStage: Record<string, number>;
  excluded: { notice: number; hemmung: number; expired: number; undetermined: number; later: number; noContract: number };
  truncated: boolean;
  noLegalAdvice: string;
}

const JSON_HEADERS = { "Content-Type": "application/json" };

/** `null` bei 404 — Schalter aus oder nichts gefunden; das Portal blendet den Teil dann aus. */
const readOrNull = async <T>(url: string): Promise<T | null> => {
  const response = await authenticatedFetch(url, { method: "GET", headers: JSON_HEADERS });

  if (response.ok) return response.json();
  if (response.status === 404) return null;
  if (response.status === 401) throw new UnauthorizedError();

  let errorMessage = `${response.status} ${response.statusText}`;
  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.error || errorMessage;
  } catch {
    // response body not JSON — keep default message
  }
  throw new ApplicationError(errorMessage);
};

export const useClaimsAPI = (customer: string) => {
  const base = `${configuration.serverUrl}/${customer}`;

  return {
    getDefectLimitation: (defect: string) =>
      readOrNull<DefectLimitation>(`${base}/defects/${encodeURIComponent(defect)}/limitation`),

    getContract: (contract: string) =>
      readOrNull<ConstructionContractView>(`${base}/claims/contracts/${encodeURIComponent(contract)}`),

    /** Hat der Mandant Bauverträge? Ohne (oder Schalter aus) zeigt das Portal das Radar nicht. */
    hasContracts: async (): Promise<boolean> => {
      const result = await readOrNull<{ contracts: unknown[] }>(`${base}/claims/contracts`);

      return Array.isArray(result?.contracts) && result!.contracts.length > 0;
    },

    listClaimsAtRisk: (days: number) => readOrNull<ClaimsAtRisk>(`${base}/claims/at-risk?days=${days}`),
  };
};
